import { memo } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position } from '@xyflow/react'
import '../../shared/components/upscale.css'
import { WwLoader } from '../../shared/components/WwLoader'
import { BOOK_GLYPH, SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The Library cards (Huanrui, 2026-10-13; Internal / External, 2026-10-14)
 * -------------------------------------------------------------
 * A card on the board only, with no input (an output, wired to the frames
 * it puts down): words, a library -- Audit,
 * Visual Language, Design References or Amazon (United States) -- and how
 * many. Search puts down a frame with that many pictures in rows of one
 * height, picked blind: the card never shows the library's pictures, it
 * just fetches some. A library's are drawn at random from its first 200
 * matches, so searching again brings different ones; Amazon's are the
 * first results, in Amazon's order (each search costs Amazon credits; a
 * week's repeat is free).
 */

/*
 * TWO CARDS SINCE 2026-10-14 (Huanrui): Internal Library -- Audit, Visual
 * Language and Design References together, no choice to make -- and
 * External Library, with Amazon, Google or Unsplash to choose from. One node
 * type, told apart by `kind`. A card from before keeps working: its single
 * library is searched as it was.
 */
export type LibraryKind = 'internal' | 'external'

export type LibrarySource =
  | 'internal'
  | 'amazon'
  | 'google'
  | 'unsplash'
  // Cards from before 2026-10-14, each on one library.
  | 'audit'
  | 'visual-language'
  | 'design-works'

// Unsplash taken out 2026-10-27 (Huanrui); a card left on it searches Google (sourceOf).
export const EXTERNAL_CARD_SOURCES: { value: LibrarySource; label: string }[] = [
  { value: 'amazon', label: 'Amazon' },
  { value: 'google', label: 'Google' },
]

/** The card's source as it is searched now: Unsplash, no longer offered, is Google. */
export function sourceOf(data: { kind?: LibraryKind; source?: LibrarySource }): LibrarySource {
  if (data.source === 'unsplash') return 'google'
  return data.source ?? (kindOf(data) === 'external' ? 'google' : 'internal')
}

/** The Internal Library card's choice of libraries (2026-10-14). */
export const INTERNAL_CARD_SCOPES: { value: LibrarySource; label: string }[] = [
  { value: 'internal', label: 'All' },
  { value: 'audit', label: 'Audit' },
  { value: 'design-works', label: 'Reference' },
  { value: 'visual-language', label: 'Language' },
]

/** What a frame from this source is called, before the words. */
export const SOURCE_NAMES: Record<LibrarySource, string> = {
  internal: 'Internal Library',
  amazon: 'Amazon',
  google: 'Google Images',
  unsplash: 'Unsplash',
  audit: 'Audit',
  'visual-language': 'Visual Language',
  'design-works': 'Design References',
}

export const LIBRARY_COUNTS = [10, 20, 30, 40, 50, 60]

/** Which card this is, for a card from before `kind` was kept. */
export function kindOf(data: { kind?: LibraryKind; source?: LibrarySource }): LibraryKind {
  if (data.kind) return data.kind
  return data.source === 'amazon' || data.source === 'google' || data.source === 'unsplash' ? 'external' : 'internal'
}

export interface LibraryNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  glow: string
  open?: boolean
  /** The words. Kept as `prompt`, like every card's typing. */
  prompt?: string
  kind?: LibraryKind
  source?: LibrarySource
  /** How many. Kept as `n`. */
  n?: number
  running?: boolean
  error?: string

  /* handed in by the board, never stored */
  onEdit?: (patch: Partial<LibraryNodeData>) => void
  onRun?: () => void
  /** Browse full screen (LibraryBrowser.tsx, 2026-10-16): only on a card on the board. */
  onBrowse?: () => void
}

export const LibraryNode = memo(function LibraryNode({ data, selected }: NodeProps) {
  const card = data as LibraryNodeData
  const isOpen = card.open !== false
  const running = card.running === true
  const kind = kindOf(card)
  const source = sourceOf(card)
  const n = card.n ?? 20
  const words = card.prompt ?? ''
  const edit = (patch: Partial<LibraryNodeData>) => card.onEdit?.(patch)
  const run = () => {
    if (!running && words.trim()) card.onRun?.()
  }

  return (
    <div
      className={[
        'cnode',
        'pnode',
        'xnode',
        'lnode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        running ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      {/* No input; an output, wired to every frame its searches put down (2026-10-13). */}
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={kind === 'internal' ? BOOK_GLYPH : card.glyph} />
        </span>
        <span className="cnode__name">{card.name}</span>
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => edit({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {isOpen ? (
        <div className="pnode__body">
          <div className="lnode__row">
          <input
            className="lnode__search nodrag"
            value={words}
            placeholder={
              kind === 'internal'
                ? 'Search every library'
                : `Search ${SOURCE_NAMES[source] ?? 'the web'}`
            }
            aria-label="Search words"
            onChange={(event) => edit({ prompt: event.target.value, error: '' })}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                run()
              }
            }}
          />
          {card.onBrowse ? (
            <button
              type="button"
              className="lnode__browse nodrag"
              onClick={() => card.onBrowse?.()}
              title="Browse full screen and pick the pictures yourself"
              aria-label="Browse full screen"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M8.5 1.5h4v4M5.5 12.5h-4v-4M12.5 1.5 8 6M1.5 12.5 6 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          ) : null}
          </div>

          {kind === 'external' ? (
            <div className="pnode__field">
              <span className="pnode__label">From</span>
              <div
                className="lnode__switch nodrag"
                role="radiogroup"
                aria-label="Which library"
                style={{ gridTemplateColumns: `repeat(${EXTERNAL_CARD_SOURCES.length}, minmax(0, 1fr))` }}
              >
                {EXTERNAL_CARD_SOURCES.map((one) => (
                  <button
                    key={one.value}
                    type="button"
                    role="radio"
                    aria-checked={one.value === source}
                    className={`lnode__seg${one.value === source ? ' lnode__seg--on' : ''}`}
                    onClick={() => edit({ source: one.value, error: '' })}
                  >
                    {one.label}
                  </button>
                ))}
              </div>
              <span className="xnode__hint">
                {source === 'amazon'
                  ? 'Amazon United States · each new search uses Amazon credits'
                  : 'Google United States · each new search uses Google credits'}
              </span>
            </div>
          ) : (
            <div className="pnode__field">
              <span className="pnode__label">From</span>
              <div
                className="lnode__switch nodrag"
                role="radiogroup"
                aria-label="Which libraries"
                style={{ gridTemplateColumns: `repeat(${INTERNAL_CARD_SCOPES.length}, minmax(0, 1fr))` }}
              >
                {INTERNAL_CARD_SCOPES.map((one) => (
                  <button
                    key={one.value}
                    type="button"
                    role="radio"
                    aria-checked={one.value === source}
                    className={`lnode__seg${one.value === source ? ' lnode__seg--on' : ''}`}
                    onClick={() => edit({ source: one.value, error: '' })}
                  >
                    {one.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="pnode__field">
            <span className="pnode__label">How many</span>
            <div
              className="pnode__chips pnode__chips--grid"
              style={{ gridTemplateColumns: `repeat(${LIBRARY_COUNTS.length}, minmax(0, 1fr))` }}
            >
              {LIBRARY_COUNTS.map((one) => (
                <button
                  key={one}
                  type="button"
                  className={`pnode__chip nodrag${one === n ? ' pnode__chip--on' : ''}`}
                  onClick={() => edit({ n: one })}
                >
                  {one}
                </button>
              ))}
            </div>
          </div>

          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {isOpen || running ? (
        <button
          type="button"
          className="cnode__run pnode__run nodrag"
          onClick={run}
          disabled={running || !words.trim()}
          title={words.trim() ? 'Put a frame of pictures beside this card' : 'Type what to look for first'}
        >
          {/* Searching: the black and white loader, in the button (2026-10-13). */}
          {running ? <WwLoader width={40} variant="mono" label="Searching" /> : 'Search'}
        </button>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
